// tabs.component.ts
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit, OnDestroy, effect, input } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { Observable, firstValueFrom, Subscription } from 'rxjs';
import { CommunicationService } from '../signal-lead.serice';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingSkeletonComponent } from 'src/app/shared/loading-models/loading-tabs-views';
import { ActivityModalDataService } from '@app/services/lead-activity-modal.service';
import { SocketService } from 'src/app/services/socket.service';
import { tabsComunicationService } from '../signal-tabs-communication';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule, MatTabsModule, LoadingSkeletonComponent],
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss',
})
export class TabsComponent implements OnInit, OnDestroy {
  tabs = input<any[]>([]);
  id = input<any>();
  lead = input<any>();
  resetTabs = input<boolean>(false);
  receivedData = this.communicationService.data;
  currentIndex: number;
  isModal = input<boolean>(false);

  loadedTabs: boolean[] = [];
  tabData: any[] = [];
  paramData: any[] = [];
  loading: boolean = false;
  prevIndex: number = 0;
  previousId: any = null;

  private tabsToReset = ['Messages', 'Note', 'Activity'];
  private subscriptions = new Subscription();

  private tabsSignal$ = toObservable(this.tabsComunicationService.data);

  constructor(
    private http: HttpClient,
    private communicationService: CommunicationService,
    private route: ActivatedRoute,
    private router: Router,
    private modalDataService: ActivityModalDataService,
    private socketService: SocketService,
    private tabsComunicationService: tabsComunicationService
  ) {
    effect(() => {
      const newData = this.receivedData();
      this.onDataReceived(newData);
    });

    effect(() => {
      if (this.resetTabs()) {
        this.resetSpecificTabs();
      }
    });

    effect(() => {
      const currentId = this.id();
      if (this.previousId !== null && String(this.previousId) !== String(currentId)) {
        this.resetAllTabs();
      }
      this.previousId = currentId;
    });
  }

  ngOnInit() {
    this.loadedTabs = Array(this.tabs().length).fill(false);
    this.tabData = Array(this.tabs().length).fill(null);
    this.previousId = this.id();

    if (this.tabs().length > 0) {
      this.loadTabData(0);
    }

    // Activity events → update Activity tab cache
    this.subscriptions.add(
      this.modalDataService.activityEvents$.subscribe(activityEvent => {
        if (!activityEvent) return;

        const activityTabIndex = this.tabs().findIndex(tab => tab.label === 'Activity');
        if (activityTabIndex === -1 || !this.loadedTabs[activityTabIndex]) return;

        const cachedData = this.tabData[activityTabIndex];
        if (!cachedData || !cachedData.data) return;

        let updatedActivities = [...cachedData.data];

        if (activityEvent.action === 'bulk-update') {
          this.loadedTabs[activityTabIndex] = false;
          this.loadTabData(activityTabIndex);
          return;
        }

        if (activityEvent.action === 'create') {
          updatedActivities.unshift(activityEvent.data);
        } else if (activityEvent.action === 'update') {
          updatedActivities = updatedActivities.map(activity =>
            activity.id === activityEvent.data.id ? { ...activityEvent.data } : activity
          );
        } else if (activityEvent.action === 'delete') {
          updatedActivities = updatedActivities.filter(
            activity => activity.id !== activityEvent.data.id
          );
        } else if (activityEvent.data?.newTask) {
          updatedActivities = updatedActivities.map(activity =>
            activity.id === activityEvent.data.id ||
            activity.id === activityEvent.data?.oldTask?.data?.id
              ? { ...activityEvent.data.oldTask?.data || activityEvent.data.oldTask }
              : activity
          );
          updatedActivities.unshift(activityEvent.data.newTask);
        }

        this.tabData[activityTabIndex] = {
          ...cachedData,
          data: updatedActivities,
          total: activityEvent.action === 'create' ? cachedData.total + 1 :
                 activityEvent.action === 'delete' ? cachedData.total - 1 :
                 cachedData.total
        };
      })
    );

    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        const tabIndex = parseInt(params['section'], 10);
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < this.tabs().length) {
          this.currentIndex = tabIndex;
          this.loadTabData(this.currentIndex);
          this.removeTabQueryParam();
        }
      })
    );

    // Socket: incoming messages from leads (inbox flow)
    this.subscriptions.add(
      this.socketService.getInboxMessages().subscribe((data) => {
        const message = data.message;
        if (!message) return;

        const currentLeadId = this.lead()?.id;
        const messageLeadId = data.notification?.record_id || message.lead_id;
        if (currentLeadId && messageLeadId && Number(messageLeadId) !== Number(currentLeadId)) return;

        this.addMessageToCache(message);
      })
    );

    // Socket: direct new message events
    this.subscriptions.add(
      this.socketService.getNewMessages().subscribe((data) => {
        const message = data.message;
        if (!message) return;

        const currentLeadId = this.lead()?.id;
        if (currentLeadId && data.leadId && Number(data.leadId) !== Number(currentLeadId)) return;

        this.addMessageToCache(message);
      })
    );

    // Document-sent messages → update Messages tab cache
    this.subscriptions.add(
      this.tabsSignal$.subscribe((signal:any) => {
        if (signal?.type?.startsWith('document-') && signal?.obj?.data?.message) {
          this.addMessageToCache(signal.obj.data.message);
        }
      })
    );
  }

  /**
   * Single method to add a message to the Messages tab cache.
   * Called from socket listeners and document signal subscription.
   */
  private addMessageToCache(message: any): void {
    const msgIdx = this.tabs().findIndex(t => t.label === 'Messages');
    if (msgIdx === -1 || !this.loadedTabs[msgIdx] || !this.tabData[msgIdx]?.data) return;

    const exists = this.tabData[msgIdx].data.some(m =>
      m.id === message.id ||
      m.email_db_id === message.email_db_id ||
      m.message_id === message.message_id
    );

    if (!exists) {
      this.tabData[msgIdx] = {
        ...this.tabData[msgIdx],
        data: [message, ...this.tabData[msgIdx].data],
        total: (this.tabData[msgIdx].total || 0) + 1
      };
    }
  }

  removeTabQueryParam() {
    this.router.navigate([], {
      queryParams: { section: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  resetAllTabs() {
    this.loadedTabs = Array(this.tabs().length).fill(false);
    this.tabData = Array(this.tabs().length).fill(null);
    this.paramData = Array(this.tabs().length).fill(null);
    this.currentIndex = 0;
    this.prevIndex = 0;

    if (this.tabs().length > 0) {
      this.loadTabData(this.currentIndex);
    }
  }

  resetSpecificTabs() {
    this.tabs().forEach((tab, index) => {
      if (this.tabsToReset.includes(tab.label)) {
        this.loadedTabs[index] = false;
        this.tabData[index] = null;

        if (index === this.currentIndex) {
          this.loadTabData(index);
        }
      }
    });
  }

  async loadTabData(index: number) {
    this.prevIndex = this.currentIndex;
    this.currentIndex = index;
    if (this.loadedTabs[index]) return;

    this.loading = true;
    const tabApiUrl = this.tabs()[index].api;
    const paramsMap = this.tabs()[index].params;
    for (const key in paramsMap) {
      if (Array.isArray(paramsMap[key])) {
        paramsMap[key] = JSON.stringify(paramsMap[key]);
      }
    }
    paramsMap.id = this.id();
    this.paramData[index] = paramsMap;

    const response = await firstValueFrom(
      this.getTabData(tabApiUrl, paramsMap),
    );

    this.tabData[index] = response;
    this.loadedTabs[index] = true;
    this.loading = false;
  }

  onDataReceived(newSignal: any) {
      // ✅ NEW: invalidate & optionally reload the Documents tab
  if (newSignal.type === 'reset-documents-tab') {
    const docsIdx = this.tabs().findIndex(t => t.label === 'Documents');
    if (docsIdx !== -1) {
      this.loadedTabs[docsIdx] = false;
      this.tabData[docsIdx] = null;
      if (docsIdx === this.currentIndex) {
        this.loadTabData(docsIdx);   // already visible → reload immediately
      }
      // if not currently visible, it will lazy-reload when the user switches to it
    }
    return;
  }
  
    if (newSignal.type == 'destroy' && newSignal.obj) {
      // this.tabData[this.prevIndex] = newSignal.obj.data;
    }
  }

  getTabData(apiUrl: string, params: any): Observable<any> {
    return this.http.get(apiUrl, {
      params,
    });
  }

  getSkeletonRows(tabLabel: string): number {
    switch (tabLabel) {
      case 'Messages':
        return 5;
      case 'Note':
        return 3;
      case 'Activity':
        return 6;
      case 'Documents':
        return 4;
      case 'Changelog':
        return 7;
      default:
        return 4;
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}